import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  highestPublishedBidCents,
  MAX_CHECKOUT_CENTS,
  minimumAcceptedBidCents,
  SITE_CONFIG,
} from "../site-config";
import { formatMoney, normalizeUrl } from "../lib";
import type { CheckoutResponse, SiteId, SiteState, Slot } from "../types";
import "../styles/claim-dialog.css";

interface ClaimDialogProps {
  site: SiteId;
  slot: Slot;
  state: SiteState;
  initialUrl?: string;
  initialAmountCents?: number;
  onClose: () => void;
}

export default function ClaimDialog({
  site,
  slot,
  state,
  initialUrl = "",
  initialAmountCents,
  onClose,
}: ClaimDialogProps) {
  const config = SITE_CONFIG[site];
  const currentLeader = slot.entries[0];
  const publishedHighestBidCents = highestPublishedBidCents(state);
  const publishedMinimumBidCents = minimumAcceptedBidCents(state);
  const [serverHighestBidCents, setServerHighestBidCents] = useState(publishedHighestBidCents);
  const [serverMinimumBidCents, setServerMinimumBidCents] = useState(publishedMinimumBidCents);
  const [biddingClosed, setBiddingClosed] = useState(
    config.globalLeaderIncrementCents !== undefined && publishedMinimumBidCents > MAX_CHECKOUT_CENTS,
  );
  const highestBidCents = Math.max(publishedHighestBidCents, serverHighestBidCents);
  const minimumBidCents = Math.max(publishedMinimumBidCents, serverMinimumBidCents);
  const minimumDollars = minimumBidCents / 100;
  const maximumDollars = MAX_CHECKOUT_CENTS / 100;
  const [url, setUrl] = useState(initialUrl);
  const [amount, setAmount] = useState(String(
    Math.min(Math.max(initialAmountCents ?? minimumBidCents, minimumBidCents), MAX_CHECKOUT_CENTS) / 100,
  ));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const amountWasEdited = useRef(initialAmountCents !== undefined);
  const checkoutAttemptRef = useRef<{ fingerprint: string; id: string } | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document !== "undefined" && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    const focusableSelector = [
      "button:not([disabled])",
      "a[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = [...dialog.querySelectorAll<HTMLElement>(focusableSelector)]
        .filter((element) => !element.hasAttribute("disabled") && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedRef.current?.focus({ preventScroll: true });
    };
  }, [onClose]);

  const existingEntry = slot.entries.find((entry) => normalizeUrl(entry.url) === normalizeUrl(url));
  const existingUrlTotal = existingEntry?.amountCents ?? 0;
  const centsToBeat = Math.max(0, (currentLeader?.amountCents ?? 0) - existingUrlTotal);
  const winningAddOnCents = Math.max(
    state.minimumBidCents,
    Math.ceil((centsToBeat + 1) / 100) * 100,
  );
  const suggestedBidCents = site === "earth"
    ? minimumBidCents
    : existingEntry
      ? winningAddOnCents
      : state.minimumBidCents;

  useEffect(() => {
    if (!amountWasEdited.current) {
      setAmount(String(Math.min(suggestedBidCents, MAX_CHECKOUT_CENTS) / 100));
    }
  }, [suggestedBidCents]);

  function chooseAmount(nextAmount: string): void {
    amountWasEdited.current = true;
    setAmount(nextAmount);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const amountCents = Math.round(Number(amount) * 100);
    if (biddingClosed) {
      setError("This board has reached the maximum checkout amount supported by the payment processor.");
      return;
    }
    if (
      !Number.isSafeInteger(amountCents)
      || amountCents < minimumBidCents
      || amountCents > MAX_CHECKOUT_CENTS
      || amountCents % 100 !== 0
    ) {
      setError(
        amountCents > MAX_CHECKOUT_CENTS
          ? `Enter no more than ${formatMoney(MAX_CHECKOUT_CENTS)}.`
          : site === "earth" && highestBidCents > 0
            ? `Bid at least ${formatMoney(minimumBidCents)} — $3 above the current high of ${formatMoney(highestBidCents)}.`
            : `Enter a whole-dollar bid of at least ${formatMoney(minimumBidCents)}.`,
      );
      return;
    }
    setSubmitting(true);
    const attemptFingerprint = JSON.stringify([site, slot.id, url, amountCents]);
    if (checkoutAttemptRef.current?.fingerprint !== attemptFingerprint) {
      checkoutAttemptRef.current = {
        fingerprint: attemptFingerprint,
        id: window.crypto.randomUUID(),
      };
    }
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site,
          slotId: slot.id,
          url,
          amountCents,
          checkoutAttemptId: checkoutAttemptRef.current.id,
        }),
      });
      const result = (await response.json()) as CheckoutResponse & {
        error?: string;
        minimumAmountCents?: number;
        highestBidCents?: number;
        maximumAmountCents?: number;
        biddingClosed?: boolean;
      };
      if (!response.ok || !result.url) {
        if (
          response.status === 409 &&
          Number.isSafeInteger(result.minimumAmountCents) &&
          (result.minimumAmountCents ?? 0) > 0
        ) {
          const freshMinimumBidCents = result.minimumAmountCents as number;
          const isNowClosed = result.biddingClosed === true || freshMinimumBidCents > MAX_CHECKOUT_CENTS;
          setServerMinimumBidCents((current) => Math.max(current, freshMinimumBidCents));
          setBiddingClosed((current) => current || isNowClosed);
          if (Number.isSafeInteger(result.highestBidCents) && (result.highestBidCents ?? 0) >= 0) {
            setServerHighestBidCents((current) => Math.max(current, result.highestBidCents as number));
          }
          chooseAmount(String((isNowClosed ? MAX_CHECKOUT_CENTS : freshMinimumBidCents) / 100));
          setError(result.error ?? "The minimum bid changed. Review the new amount and continue.");
          setSubmitting(false);
          return;
        }
        throw new Error(result.error ?? "Checkout did not start.");
      }
      if (
        /^cs_(?:test_|live_)?[A-Za-z0-9]+$/.test(result.sessionId) &&
        /^[0-9a-z]{1,12}\.[0-9a-z]{1,12}\.[A-Za-z0-9_-]{43}$/.test(result.confirmationToken)
      ) {
        try {
          window.sessionStorage.setItem(`outbid-confirm:${result.sessionId}`, result.confirmationToken);
        } catch {
          // Stripe's signed webhook remains authoritative if storage is unavailable.
        }
      }
      // Always continue to an already-created Checkout Session. If the private
      // receipt handoff failed, the success view falls back to webhook processing.
      window.location.assign(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Checkout did not start.");
      setSubmitting(false);
    }
  }

  return (
    <div className="claim-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className={`claim-dialog claim-dialog-${site}`} role="dialog" aria-modal="true" aria-labelledby="claim-title" tabIndex={-1}>
        <button className="claim-close" type="button" onClick={onClose} aria-label="Close bid form">×</button>
        <div className="claim-kicker">{config.domain} · {slot.label}</div>
        <h2 id="claim-title">Bid on {slot.label}.</h2>
        <p className="claim-explainer">
          Paste your URL. Set your amount. That’s it.
          {biddingClosed
            ? " This board has reached the payment processor’s checkout ceiling, so bidding is closed."
            : site === "earth" && highestBidCents > 0
            ? ` The current sitewide high is ${formatMoney(highestBidCents)}. The next accepted bid is ${formatMoney(minimumBidCents)}.`
            : currentLeader
              ? ` The current lead is ${formatMoney(currentLeader.amountCents)}.`
              : ""}
        </p>

        <form onSubmit={submit}>
          <div className="claim-fields">
            <label className="claim-field-wide">
              <span>Website URL</span>
              <input value={url} onChange={(event) => setUrl(event.target.value)} maxLength={500} required inputMode="url" autoComplete="url" placeholder="your.website" autoFocus />
            </label>
            <label className="claim-field-wide">
              <span>Your bid (USD)</span>
              <div className="claim-money-field"><i>$</i><input value={amount} onChange={(event) => chooseAmount(event.target.value)} type="number" min={Math.min(minimumDollars, maximumDollars)} max={maximumDollars} step="1" required disabled={biddingClosed} /></div>
            </label>
          </div>

          {error && <p className="claim-error" role="alert">{error}</p>}
          {!state.stripeEnabled && <p className="claim-error" role="alert">Checkout is not connected on this deployment yet.</p>}
          <button
            className="claim-submit"
            type="submit"
            disabled={biddingClosed || submitting || !state.stripeEnabled}
            data-fast-goal="checkout_started"
            data-fast-goal-site={site}
            data-fast-goal-slot={slot.id}
          >
            {biddingClosed
              ? "Bidding closed"
              : submitting
                ? "Opening Stripe…"
                : `Continue with ${formatMoney(Math.round(Number(amount || 0) * 100))} →`}
          </button>
          <p className="claim-fineprint">
            Secure Stripe checkout. {site === "earth" ? "The $3 lead is checked when checkout opens. " : ""}Your paid total determines your rank.
          </p>
        </form>
      </section>
    </div>
  );
}
